import type { AudioChannel } from '../audio/audioManager';

// Single source of truth for the channel → color language shared by every
// dev surface that paints audio: the 3D source spheres (audioGizmos) and the
// timeline SOUND lane. Keeping the palette here means a source sphere in the
// scene and its chip on the timeline are unmistakably the same channel — the
// color can't drift between the two.
//
//   ambient   = soft green     hero-anchored world beds (fans, generator)
//   music     = cyan           the cassette / era track (from the boombox)
//   narration = warm yellow    spoken word
//   sfx       = magenta        transient one-shot events
//   room      = muted violet   persistent true-ambient bed (room tone)
export const CHANNEL_COLORS: Record<AudioChannel, number> = {
  ambient: 0x6acf91,
  music: 0x4fc3f7,
  narration: 0xffd87c,
  sfx: 0xff7eb6,
  room: 0xa98fd9,
};

/** The channel color as a CSS hex string, for DOM surfaces (timeline chips).
 *  Falls back to the neutral text color for unknown channels. */
export function channelCssColor(channel: string): string {
  const n = (CHANNEL_COLORS as Record<string, number>)[channel];
  if (n == null) return '#e6ebef';
  return '#' + n.toString(16).padStart(6, '0');
}

// B2 — the focus highlight is a GENTLE lift, not a blowout. The channel-colored
// ring is the primary identity signal; brightness is a faint tint on top.
// brightness(1.3) read as a blowout, so it's scaled back to FOCUS_BRIGHTNESS and
// the ring thinned slightly. FOCUS_BRIGHTNESS is John's by-eye knob.
const FOCUS_BRIGHTNESS = 1.12;
const FOCUS_RING_PX = 1.5;

/** The single "this sound is focused" highlight, shared by every DOM audio
 *  surface (SOUND chip, BED bar, CUE diamond, palette chip, channel fader) so
 *  a hover anywhere lights its matches identically — a channel-colored ring +
 *  a touch of brightness. Non-destructive: uses `outline`/`filter`, never the
 *  element's own `boxShadow`/`background`, so it composes with selection,
 *  era rings, and gain fills. `on=false` clears it. */
export function applyFocusGlow(el: HTMLElement, on: boolean, channel: string): void {
  const c = channelCssColor(channel);
  el.style.outline = on ? `${FOCUS_RING_PX}px solid ${c}` : '';
  el.style.outlineOffset = on ? '1px' : '';
  el.style.filter = on ? `brightness(${FOCUS_BRIGHTNESS})` : '';
}
